// FILE: src/components/FeedbackHubView.js
//
// Admin → Feedback Hub. A lightweight Jira-style console over feedback_items:
// summary cards, a searchable/filterable queue, a detail drawer with status +
// note actions, a metrics panel, and a manual-entry form for off-platform
// feedback. Registered as an admin sub-view in App.js (adminView==='feedback').
//
// Reads are admin-gated by RLS / SECURITY DEFINER RPCs (migration 040); this
// view assumes it is rendered inside AdminGate.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AdminGateContext } from '../admin/AdminGate';
import {
  listFeedback, getFeedbackSummary,
  FEEDBACK_CATEGORIES, FEEDBACK_STATUSES,
} from '../../services/feedbackService';
import { getActiveCertIds } from '../../config/exams';
import FeedbackDetailPanel from './FeedbackDetailPanel';
import FeedbackMetricsPanel from './FeedbackMetricsPanel';
import ManualFeedbackForm from './ManualFeedbackForm';
import {
  styles, pill, STATUS_COLORS, SEVERITY_COLORS, formatDate, certLabel,
} from './feedbackStyles';

const SUMMARY_CARDS = [
  { key: 'new',         label: 'New' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'released',    label: 'Released' },
  { key: 'closed',      label: 'Closed' },
];

export default function FeedbackHubView({ onBack }) {
  const auth = useContext(AdminGateContext);
  const adminEmail = auth?.userEmail || null;

  const [tab, setTab]         = useState('queue');
  const [items, setItems]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selected, setSelected] = useState(null);

  // Filters
  const [search, setSearch]   = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fCat, setFCat]       = useState('');
  const [fCert, setFCert]     = useState('');

  const refreshSummary = useCallback(() => {
    getFeedbackSummary().then(({ data }) => setSummary(data));
  }, []);

  const loadList = useCallback(() => {
    setLoading(true); setError(null);
    listFeedback({ status: fStatus, category: fCat, certification: fCert, search }).then(({ data, error: e }) => {
      if (e) setError(e.message || 'Failed to load feedback');
      setItems(data);
      setLoading(false);
    });
  }, [fStatus, fCat, fCert, search]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);
  useEffect(() => {
    const t = setTimeout(loadList, search ? 250 : 0); // debounce search typing
    return () => clearTimeout(t);
  }, [loadList, search]);

  // Patch a single row in place after a detail-panel update.
  function handleUpdated(updated) {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    setSelected(updated);
    refreshSummary();
  }

  function handleManualCreated() {
    setTab('queue');
    loadList();
    refreshSummary();
  }

  const certs = getActiveCertIds();

  return (
    <div data-testid="feedback-hub" style={styles.wrapper}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={onBack} data-testid="feedback-hub-back">← Back</button>
        <span style={styles.headerTitle}>💬 Feedback Hub</span>
        {adminEmail && <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{adminEmail}</span>}
      </div>

      <div style={styles.body}>
        {/* ── Summary cards ─────────────────────────────────── */}
        <div style={styles.cardRow}>
          {SUMMARY_CARDS.map((c) => (
            <div key={c.key} style={styles.card}>
              <div style={styles.cardNum}>{summary ? (summary[c.key] ?? 0) : '—'}</div>
              <div style={styles.cardLabel}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* ── Tabs ──────────────────────────────────────────── */}
        <div style={styles.tabRow}>
          <button style={styles.tab(tab === 'queue')} onClick={() => setTab('queue')} data-testid="feedback-tab-queue">Queue</button>
          <button style={styles.tab(tab === 'metrics')} onClick={() => setTab('metrics')} data-testid="feedback-tab-metrics">Metrics</button>
          <button style={styles.tab(tab === 'manual')} onClick={() => setTab('manual')} data-testid="feedback-tab-manual">Add manual</button>
        </div>

        {tab === 'metrics' && <FeedbackMetricsPanel />}
        {tab === 'manual' && <ManualFeedbackForm onCreated={handleManualCreated} />}

        {tab === 'queue' && (
          <>
            {/* ── Filters ─────────────────────────────────── */}
            <div style={styles.filterBar}>
              <input
                style={{ ...styles.input, minWidth: '220px' }}
                placeholder="Search description / title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="feedback-search"
              />
              <select style={styles.select} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="feedback-filter-status">
                <option value="">All statuses</option>
                {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select style={styles.select} value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="feedback-filter-category">
                <option value="">All categories</option>
                {FEEDBACK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select style={styles.select} value={fCert} onChange={(e) => setFCert(e.target.value)} data-testid="feedback-filter-cert">
                <option value="">All certs</option>
                {certs.map((c) => <option key={c} value={c}>{certLabel(c)}</option>)}
              </select>
              <button style={styles.secondaryBtn} onClick={loadList}>Refresh</button>
            </div>

            {error && <p style={{ color: '#c62828' }}>{error}</p>}

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Sev</th>
                  <th style={styles.th}>Cert</th>
                  <th style={styles.th}>Page</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Description</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    style={styles.row(selected?.id === it.id)}
                    onClick={() => setSelected(it)}
                    data-testid="feedback-row"
                  >
                    <td style={styles.td}>{formatDate(it.created_at)}</td>
                    <td style={styles.td}>{it.category}</td>
                    <td style={styles.td}><span style={pill(SEVERITY_COLORS, it.severity)}>{it.severity}</span></td>
                    <td style={styles.td}>{certLabel(it.certification)}</td>
                    <td style={styles.td}>{it.page_name || '—'}</td>
                    <td style={styles.td}><span style={pill(STATUS_COLORS, it.status)}>{it.status}</span></td>
                    <td style={{ ...styles.td, maxWidth: '320px' }}>
                      {(it.description || '').slice(0, 120)}{(it.description || '').length > 120 ? '…' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!loading && items.length === 0 && (
              <div style={styles.empty}>No feedback matches these filters yet.</div>
            )}
            {loading && <div style={styles.empty}>Loading…</div>}
          </>
        )}
      </div>

      {selected && (
        <FeedbackDetailPanel
          item={selected}
          adminEmail={adminEmail}
          onUpdated={handleUpdated}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
